import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";

// Convert base64url VAPID public key to Uint8Array (required by PushManager.subscribe)
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Web Push subscription toggle for the Client Portal.
 * Renders a single bell button in the header.
 */
export function PushNotificationsToggle() {
  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  const [permission, setPermission] = useState(() => (supported ? Notification.permission : "denied"));
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const refreshSubscription = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      if (!reg) return setSubscribed(false);
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch {
      setSubscribed(false);
    }
  }, [supported]);

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.register("/sw.js").catch(() => { /* ignore */ });
    refreshSubscription();
  }, [supported, refreshSubscription]);

  const enable = async () => {
    if (!supported) return;
    setBusy(true);
    try {
      // iOS Safari: Web Push funziona solo se la PWA è installata (Aggiungi a Home).
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
      if (isIOS && !isStandalone) {
        toast.error("Su iPhone/iPad devi prima installare la web app: apri il menu Condividi → 'Aggiungi alla schermata Home', poi riprova da lì.");
        return;
      }

      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Permesso notifiche negato dal browser. Abilitalo dalle impostazioni del sito e riprova.");
        return;
      }

      // Registra il service worker e attendi che sia attivo
      await navigator.serviceWorker.register("/sw.js");
      const reg = await navigator.serviceWorker.ready;

      // Recupera la VAPID public key dal backend
      const { data } = await api.get("/push/vapid-public-key");
      if (!data?.public_key) {
        toast.error("Chiave VAPID non configurata sul server. Contatta l'agenzia.");
        return;
      }
      const applicationServerKey = urlBase64ToUint8Array(data.public_key);

      // Se esiste già una sottoscrizione con una chiave diversa, rimuovila prima
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        try { await existing.unsubscribe(); } catch { /* ignore */ }
      }

      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
      const key = sub.toJSON();
      await api.post("/my/push-subscription", {
        endpoint: key.endpoint,
        keys: key.keys,
      });
      setSubscribed(true);
      toast.success("Notifiche attivate ✓");
    } catch (e) {
      // Superficie il vero motivo del fallimento (browser, server o rete)
      const msg = e?.response?.data?.detail || e?.message || String(e);
      // Errori tipici del PushManager
      if (/permission/i.test(msg) || e?.name === "NotAllowedError") {
        toast.error("Permesso notifiche negato dal browser. Abilitalo dalle impostazioni del sito.");
      } else if (/AbortError|InvalidStateError/i.test(msg) || /subscribe/i.test(msg)) {
        toast.error(`Impossibile attivare le notifiche: ${msg}. Prova a chiudere e riaprire il browser.`);
      } else {
        toast.error(`Impossibile attivare le notifiche: ${msg}`);
      }
      console.error("[push] enable error", e);
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api.delete(`/my/push-subscription`, { params: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notifiche disattivate");
    } catch (e) {
      toast.error("Errore nella disattivazione");
    } finally {
      setBusy(false);
    }
  };

  if (!supported) return null;

  const active = subscribed && permission === "granted";

  return (
    <button
      type="button"
      data-testid="push-toggle"
      onClick={active ? disable : enable}
      disabled={busy}
      title={active ? "Disattiva notifiche push" : "Attiva notifiche push"}
      aria-pressed={active}
      className={`text-xs px-3 py-1.5 rounded inline-flex items-center gap-1.5 transition-colors ${
        active
          ? "bg-emerald-500/20 text-emerald-200 border border-emerald-300/40 hover:bg-emerald-500/30"
          : "bg-white/10 text-slate-300 hover:bg-white/20"
      } disabled:opacity-50`}
    >
      {active ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
      {active ? "Notifiche ON" : "Notifiche"}
    </button>
  );
}

export default PushNotificationsToggle;
